import { Component, computed, EventEmitter, HostBinding, Input, Output, signal, ViewChild } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  AddressModelKeys,
  AssociationKeys,
  AT_LEISURE_EXCURSION_SUFFIX,
  ATTENDEE_TYPE_LOOKUP,
  ATTENDEE_WIZARD_STATE_LOOKUP,
  AttendeeKeys,
  AttendeeType,
  BUSINESS_PERSONAL_TYPE_LOOKUP,
  ChangeSourceType,
  Conference,
  CONFERENCE_YEARS_LIST,
  ConferenceKeys,
  Constants,
  DietaryConsiderationKeys,
  EmailAddressKeys,
  EmailTemplateConfigurationKeys,
  EmailTemplates,
  EmailTemplatesDetailsMap,
  EmailTemplatesModels,
  Entity,
  EntityPermissionActivityKeys,
  EntityPermissionModelKeys,
  ExcursionKeys,
  ExportConstants,
  INVITEE_AGE_GROUP_LOOKUP,
  INVITEE_OUTCOME_STATUS_LOOKUP,
  INVITEE_STATUS_LOOKUP,
  InviteeStatus,
  LookupKeys,
  PHONE_NUMBER_TYPE_LOOKUP,
  QUALIFIED_AS_LOOKUP,
  Registrant,
  RegistrantKeys,
  RegistrantModelKeys,
  RegistrantWizardStateKeys,
  REGISTRATION_TYPE_LOOKUP,
  TRUE_FALSE_LOOKUP,
  TShirtSizesKeys,
  YES_NO_TYPE,
} from 'ag-common-lib/public-api';
import { ModalWindowComponent } from 'ag-common-svc/lib/components/modal-window/modal-window.component';
import { AgencyService, ConferenceRegistrantsService, LookupsService } from 'ag-common-svc/public-api';
import { DxDataGridComponent } from 'devextreme-angular';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, shareReplay } from 'rxjs';
import { AttendeesExportService, RegistrantsExportType, ReportGroup } from '../../../attendees-export.service';
import { AttendeesListService } from '../../../attendees-list.service';
import DataSource from 'devextreme/data/data_source';
import ArrayStore from 'devextreme/data/array_store';
import { ItemClickEvent } from 'devextreme/ui/drop_down_button';
import { AttendeeDetailsModalComponent } from '../attendee-details-modal/attendee-details-modal.component';
import { Workbook } from 'exceljs';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { saveAs } from 'file-saver-es';
import { AddAttendeesModalComponent } from './add-attendees-modal/add-attendees-modal.component';
import { UpdateInviteeOutcomeStatusModalComponent } from './update-invitee-outcome-status-modal/update-invitee-outcome-status-modal.component';
import { CloudFunctionsService } from 'ag-common-svc/lib/services/cloud-functions.service';

import { AttendeesEmailsService } from '../../../attendees-emails.service';
import { ConferenceRegistrationEmailCampaignService } from 'ag-common-svc/lib/services/email-campaigns/conference-registration-email-campaign.service';
import {
  DxFilterOperators,
  NotificationOptInGroup,
  NotificationOptInGroupKeys,
  NotificationOptInGroupType,
} from 'ag-common-lib/lib';
import { NotificationSchedulerEditModalComponent } from 'ag-common-svc/lib/components/administration/notifications-hub/notifications-scheduler/notification-scheduler-edit-modal/notification-scheduler-edit-modal.component';
import { convertFilter } from 'ag-common-svc/lib/utils/filter-builder-converter.utils';
import { ConferenceAttendeeGridFieldsService } from 'ag-common-svc/lib/services/agent-grid-fields/conference-attendee-grid-fields.service';
import { Attendee } from 'ag-common-svc/lib/utils/attendees';
import { EventNameTitlePipe } from 'ag-common-svc/shared/pipes/conference-registration/event-name-title.pipe';
import { custom } from 'devextreme/ui/dialog';
import { ExcursionInfoTableDataKeys } from 'ag-common-svc/lib/components/excursions-info-table/excursions-info-table.models';
import { BaseModelKeys } from 'ag-common-lib/lib/models/base.model';
import { filterTemplatesByDataModel } from 'ag-common-svc/lib/utils/mailer.utils';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import {
  createDiacriticsFilters,
  normalizeSearch,
} from 'ag-common-svc/shared/utils/search-in-object-diacritics-insensitive';
import { format } from 'date-fns';
import { ChairmanExtensionConfigurationKeys } from 'ag-common-lib/lib/models/registration/conference-chairman-extension';
import { RegistrantChairmanExtensionKeys } from 'ag-common-lib/lib/models/registration/registrant-chairman-extension';

@UntilDestroy()
@Component({
  selector: 'ag-crm-attendees-list-modal',
  templateUrl: './attendees-list-modal.component.html',
  styleUrls: ['./attendees-list-modal.component.scss'],
  providers: [
    AttendeesListService,
    ConferenceRegistrationEmailCampaignService,
    AttendeesEmailsService,
    EventNameTitlePipe,
    ConferenceRegistrantsService,
  ],
})
export class AttendeesListModalComponent {
  @HostBinding('class') className = 'attendees-list-modal';
  @ViewChild('attendeeDetailsModalRef', { static: true }) attendeeDetailsModalComponent: AttendeeDetailsModalComponent;
  @ViewChild('attendeesListModalRef', { static: true }) attendeesListModalComponent: ModalWindowComponent;
  @ViewChild('attendeesReportYears', { static: true }) attendeesReportYearsModal: ModalWindowComponent;
  @ViewChild('registrantGrid', { static: false }) registrantGrid: DxDataGridComponent;
  @ViewChild('addAttendeesModalRef', { static: false }) addAttendeesModalComponent: AddAttendeesModalComponent;
  @ViewChild('notificationSchedulerEditModalRef', { static: false })
  protected notificationSchedulerEditModalComponent: NotificationSchedulerEditModalComponent;
  @ViewChild('updateInviteeOutcomeStatusModalRef', { static: false })
  protected updateInviteeOutcomeStatusModalComponent: UpdateInviteeOutcomeStatusModalComponent;

  @Input() inProgress = false;
  @Output() registrantsChange = new EventEmitter<Registrant[]>();

  attendees: Promise<Attendee[]>;
  eventNameTitle: string;

  updateInviteeOutcomePopupVisibleSig = signal<boolean>(false);
  protected readonly updateInviteeOutcomePopupOptions = {
    text: 'OK',
    onClick: () => this.updateInviteeOutcomePopupVisibleSig.set(false),
  };

  agentEmail$ = new BehaviorSubject<string>(null);
  protected EntityPermissionActivityKeys = EntityPermissionActivityKeys;
  emailTypeLookup$ = this.lookupsService.emailTypeLookup$;
  tShirtUnisexLookup$ = this.lookupsService.tShirtUnisexLookup$;
  shirtGenderLookup$ = this.lookupsService.shirtGenderLookup$;
  associationTypeLookup$ = this.lookupsService.associationTypeLookup$;
  prefixesLookup$ = this.lookupsService.prefixesLookup$;
  suffixesLookup$ = this.lookupsService.suffixesLookup$;
  genderTypeLookup$ = this.lookupsService.gendersLookup$;
  dietaryConsiderationTypesLookup$ = this.lookupsService.dietaryConsiderationTypesLookup$;
  agencies$ = this.agencyService.getList().pipe(shareReplay(1));

  attendees$ = combineLatest([this.attendeesListService.attendees$, this.agencies$]).pipe(
    map(([attendees = [], agencies = []]) => {
      // lookup: agency_id -> agency.name
      const agencyMap = new Map(agencies.map(a => [a.agency_id, a.name]));

      const enrichedData = attendees.map(attendee => {
        return {
          ...attendee,
          // only for search
          __mgaSearch: normalizeSearch(agencyMap.get(attendee[AttendeeKeys.mgaId])),
          __agencySearch: normalizeSearch(agencyMap.get(attendee[AttendeeKeys.agencyId])),
        };
      });

      return new DataSource({
        paginate: true,
        store: new ArrayStore({
          key: AttendeeKeys.dbId,
          data: enrichedData,
        }),
      });
    }),
    shareReplay(1),
  );

  conferenceDbId$ = this.attendeesListService.conferenceDbId$;

  protected readonly isChairmanExtensionEnabledSig = computed(() => {
    const conference = this.attendeesListService.conferenceSig();
    const isChairmanExtensionEnabled = conference?.[ConferenceKeys.isChairmanExtensionEnabled];

    return isChairmanExtensionEnabled;
  });

  attendeesReportYearsValue: Array<Date | number | string> = [...CONFERENCE_YEARS_LIST];

  exportInProgress$ = this.exportService.exportInProgress$;
  availableReports$ = this.attendeesListService.conference$.pipe(
    map(conference => {
      const { hotelEnabled, flightsEnabled } = conference;

      const reports = structuredClone(this.exportService.availableReports);

      if (!hotelEnabled) {
        reports[0].items = reports[0].items.filter(item => item.id !== ReportGroup.HotelInfoReports);
      }

      if (!flightsEnabled) {
        reports[0].items = reports[0].items.filter(item => item.id !== ReportGroup.TravelInfoReports);
      }

      return reports;
    }),
  );

  isExportLoading = false;
  private _loaderTimeout;

  protected readonly phoneMask = Constants.PHONE_MASK;
  protected readonly AttendeeKeys = AttendeeKeys;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly AttendeeType = AttendeeType;
  protected readonly AssociationKeys = AssociationKeys;
  protected readonly AddressModelKeys = AddressModelKeys;
  protected readonly EmailAddressKeys = EmailAddressKeys;
  protected readonly TShirtSizesKeys = TShirtSizesKeys;
  protected readonly DietaryConsiderationKeys = DietaryConsiderationKeys;
  protected readonly LookupKeys = LookupKeys;
  protected readonly yesNoType = YES_NO_TYPE;
  protected readonly BUSINESS_PERSONAL_TYPE_LOOKUP = BUSINESS_PERSONAL_TYPE_LOOKUP;
  protected readonly PHONE_NUMBER_TYPE_LOOKUP = PHONE_NUMBER_TYPE_LOOKUP;
  protected readonly INVITEE_AGE_GROUP_LOOKUP = INVITEE_AGE_GROUP_LOOKUP;
  protected readonly INVITEE_STATUS_LOOKUP = INVITEE_STATUS_LOOKUP;
  protected readonly INVITEE_OUTCOME_STATUS_LOOKUP = INVITEE_OUTCOME_STATUS_LOOKUP;
  protected readonly emptyMessage: string = 'No Attendees Currently Exist';
  protected readonly dateFormat: string = Constants.DISPLAY_DATE_FORMAT;
  protected readonly InviteeStatus = InviteeStatus;
  protected readonly attendeeTypeLookup = ATTENDEE_TYPE_LOOKUP;
  protected readonly trueFalseLookup = TRUE_FALSE_LOOKUP;
  protected readonly qualifiedAsLookup = QUALIFIED_AS_LOOKUP;
  protected readonly registrationTypeLookup = REGISTRATION_TYPE_LOOKUP;
  protected readonly attendeeWizardStateLookup = ATTENDEE_WIZARD_STATE_LOOKUP;
  protected readonly YES_NO_TYPE_LOOKUP = [YES_NO_TYPE.Yes, YES_NO_TYPE.No];
  protected readonly CONFERENCE_YEARS_LIST = CONFERENCE_YEARS_LIST;
  protected readonly RegistrantKeys = RegistrantKeys;
  protected conferenceEmailTemplates$ = this.attendeesListService.conference$.pipe(
    map(conference => {
      const emailTemplates = conference?.[ConferenceKeys.emailTemplates];

      return filterTemplatesByDataModel(EmailTemplatesModels.registrant, emailTemplates);
    }),
    shareReplay(1),
  );

  readonly filterPanelText$ = new BehaviorSubject<string>('');

  filters: Record<string, any>;

  ngOnInit() {
    this.filters = createDiacriticsFilters({
      fields: [
        AttendeeKeys.firstName,
        AttendeeKeys.lastName,
        AttendeeKeys.awardName,
        AttendeeKeys.companyReference,
        [AttendeeKeys.emergencyContact, AssociationKeys.firstName].join('.'),
        [AttendeeKeys.emergencyContact, AssociationKeys.lastName].join('.'),
        [AttendeeKeys.dietaryConsideration, DietaryConsiderationKeys.dietaryConsiderationType].join('.'),
      ],
    });
  }

  protected calculateChairmanExtAttendingDecisionValue = (attendee: Partial<Attendee>) => {
    const currentValue = attendee?.[AttendeeKeys.chairmanExtAttendingDecision];
    const isInvitedToChairmanExt = attendee?.[AttendeeKeys.invitedChairmanExt];
    const isGuest = attendee?.[AttendeeKeys.attendeeType] === AttendeeType.Guest;
    const isCanceledGuest = attendee?.[AttendeeKeys.inviteeOutcomeStatus] === InviteeStatus.cancelled;

    // Display-only override: for canceled chairman-extension guests show "Declined" on the grid.
    if (isGuest && isInvitedToChairmanExt && isCanceledGuest) {
      return InviteeStatus.declined;
    }

    return currentValue;
  };

  constructor(
    private readonly lookupsService: LookupsService,
    private agencyService: AgencyService,
    public exportService: AttendeesExportService,
    private attendeesListService: AttendeesListService,
    private cloudFunctionsService: CloudFunctionsService,
    private attendeesEmailsService: AttendeesEmailsService,
    private conferenceAttendeeGridFieldsService: ConferenceAttendeeGridFieldsService,
    private eventNameTitlePipe: EventNameTitlePipe,
  ) {}

  protected onOptionChanged = (event): void => {
    if (event.name === 'searchPanel') {
      this.filterPanelText$.next(event.value);
    }
  };

  protected emailTemplateDisplayExpr = item => {
    const template = item?.[EmailTemplateConfigurationKeys.templateName] ?? item?.templateName;
    const templateConfiguration = EmailTemplatesDetailsMap.get(template);
    const title = [templateConfiguration?.title, templateConfiguration?.forEvent].filter(Boolean).join(' ');
    const activitiesDay = item?.[EmailTemplateConfigurationKeys.payload]?.activitiesDay;

    if (
      [
        EmailTemplates.notifyEmailInviteesActivities,
        EmailTemplates.notifyEmailInviteesActivitiesFirstDay,
        EmailTemplates.notifyEmailInviteesActivitiesSecondDay,
      ].includes(template) &&
      activitiesDay
    ) {
      return `${title} - ${format(new Date(activitiesDay), 'EEEE, LLLL d, yyyy')}`;
    }

    if (title) {
      return title;
    }

    const subject = item?.[EmailTemplateConfigurationKeys.subject];
    const eventName = this.attendeesListService.conferenceSig()?.[ConferenceKeys.eventName];
    const templateNameFromSubject = this.getTemplateNameFromSubject(subject, eventName);

    return templateNameFromSubject || template;
  };

  private getTemplateNameFromSubject(subject: string | undefined, eventName: string | undefined): string | undefined {
    if (!subject) {
      return undefined;
    }
    // dynamic templates title was created from 'event name - template name' in EmailTemplateConfigurationFormService
    const eventPrefix = eventName ? `${eventName} - ` : '';
    if (eventPrefix && subject.startsWith(eventPrefix)) {
      return subject.slice(eventPrefix.length)?.trim() || undefined;
    }

    return subject;
  }

  showModal(data: Conference): void {
    this.attendeesListService.setConferenceDbId(data?.[BaseModelKeys.dbId]);
    const eventName = data?.[ConferenceKeys.eventName];
    const caption = 'Event Attendees';

    this.eventNameTitle = this.eventNameTitlePipe.transform(eventName, caption + Constants.EVENT_NAME_TITLE, caption);
    this.attendeesListModalComponent?.showModal();
  }

  showAddAttendeesModal = async () => {
    const conferenceDbId = await firstValueFrom(this.conferenceDbId$);
    const attendees: Attendee[] = await firstValueFrom(this.attendeesListService.attendees$);

    this.addAttendeesModalComponent?.showModal(conferenceDbId, attendees);
  };

  updateBlankInviteeOutcomeAsAtteended = async () => {
    const attendees = await this.getFilteredAttendeesFromDataSource();
    const attendeesWithInviteeType = attendees.filter(
      attendee =>
        attendee?.[AttendeeKeys.attendeeType] === AttendeeType.Invitee &&
        !attendee?.[AttendeeKeys.inviteeOutcomeStatus],
    );

    if (!attendeesWithInviteeType?.length) {
      this.updateInviteeOutcomePopupVisibleSig.set(true);
      return;
    }

    const conferenceDbId = await firstValueFrom(this.conferenceDbId$);

    this.updateInviteeOutcomeStatusModalComponent?.showModal({
      attendees: attendeesWithInviteeType,
      conferenceDbId,
      defaultOutcomeStatus: InviteeStatus.attended,
    });
  };

  private async getFilteredAttendeesFromDataSource(): Promise<Attendee[]> {
    const dataSource = this.registrantGrid.instance.getDataSource();
    const gridFilter = this.registrantGrid.instance.getCombinedFilter(true);

    if (dataSource && typeof dataSource.store?.().load === 'function') {
      const result = await dataSource.store().load({ filter: gridFilter || undefined });
      if (Array.isArray(result)) {
        return result.filter(r => r && typeof r === 'object' && 'attendeeType' in r);
      }

      if (result && typeof result === 'object' && 'data' in result && Array.isArray(result.data)) {
        return result.data.filter(r => r && typeof r === 'object' && 'attendeeType' in r);
      }

      return [];
    }

    return await firstValueFrom(this.attendeesListService.attendees$);
  }

  editAttendee = data => {
    this.attendeeDetailsModalComponent.showModal(data);
  };

  getCellInfo(cell) {
    console.log('cell', cell);
    debugger;
  }

  async getCustomReport(e) {
    if (e.itemData?.reportType === RegistrantsExportType.productionByQuarter) {
      this.attendeesReportYearsModal.showModal();
      return;
    }

    if (!e.itemData?.reportType) {
      return;
    }
    this.isExportLoading = true;
    const conferenceDbId = await firstValueFrom(this.conferenceDbId$);
    const attendeeData = await firstValueFrom(this.attendeesListService.attendees$.pipe());
    await this.exportService.runCustomExport(conferenceDbId, e.itemData?.reportType, attendeeData).finally(() => {
      clearTimeout(this._loaderTimeout);
      this._loaderTimeout = setTimeout(() => {
        this.isExportLoading = false;
      }, 1500);
    });
  }

  async runByQuarterReport() {
    this.attendeesReportYearsModal.hideModal();
    const attendeeData = await firstValueFrom(this.attendeesListService.attendees$.pipe());

    const conferenceDbId = await firstValueFrom(this.conferenceDbId$);
    await this.exportService.runCustomExport(
      conferenceDbId,
      RegistrantsExportType.productionByQuarter,
      attendeeData,
      this.attendeesReportYearsValue,
    );
    this.attendeesReportYearsValue = [...CONFERENCE_YEARS_LIST];
  }

  protected onRowRemoving = async (data: Attendee) => {
    const dialog = custom({
      showTitle: false,
      messageHtml: 'Are you sure you want to delete this record?',
      buttons: [
        {
          text: 'Yes',
          onClick: () => {
            return true;
          },
        },
        {
          text: 'No',
          onClick: () => {
            return false;
          },
        },
      ],
    });

    const result = await dialog.show();

    if (!result) {
      return;
    }

    this.registrantGrid.instance.beginCustomLoading('Deleting...');

    const conferenceDbId = data?.[AttendeeKeys.conferenceDbId];
    const registrantDbId = data?.[BaseModelKeys.dbId];
    const actionName = ChangeSourceType.regularArm;
    await this.cloudFunctionsService.deleteConferenceRegistration({ conferenceDbId, registrantDbId, actionName });
    this.registrantGrid.instance.endCustomLoading();
  };

  protected onExporting(e: DxDataGridTypes.ExportingEvent) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Employees');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        excelCell.font = ExportConstants.EXCEL_CELL_FONT_WITH_ACCENTS;
        if (gridCell.rowType === 'data' && gridCell.column.dataField === 'ccEmail') {
          excelCell.value = gridCell.data?.['registrant']?.['data']?.[RegistrantKeys.ccEmail] ?? '';
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
      });
    });
  }

  checkIsAttendeeDetailsButtonVisible = ({ row: { data } }): boolean =>
    data?.[AttendeeKeys.attendeeType] === AttendeeType.Invitee;

  checkIsWizardStateIsSubmitted = ({ row: { data } }): boolean =>
    (data?.[AttendeeKeys.attendeeType] === AttendeeType.Invitee &&
      data?.registrant?.[RegistrantModelKeys.wizardState]?.[RegistrantWizardStateKeys.isSubmitted]) ??
    false;

  protected sendToEmail = async (e: ItemClickEvent, attendee: Attendee) => {
    const conference = await firstValueFrom(
      this.attendeesListService.conference$.pipe(filter(data => Boolean(data?.[BaseModelKeys.dbId]))),
    );
    const registrant = attendee?.registrant;

    if (!this.shouldSendEmailToRegistrant(e.itemData, registrant, conference)) {
      return;
    }

    await this.attendeesEmailsService.sendEmails(e.itemData, { registrants: [registrant] });
  };

  protected sendBulkEmails = async (e: ItemClickEvent): Promise<void> => {
    const dataSource = this.registrantGrid.instance.getDataSource();
    const gridFilter = this.registrantGrid.instance.getCombinedFilter(true);
    const filter: any[] = [[AttendeeKeys.attendeeType, DxFilterOperators.equal, AttendeeType.Invitee]];
    const conferenceDbId = await firstValueFrom(this.conferenceDbId$);
    const optInGroup = Object.assign({}, new NotificationOptInGroup(NotificationOptInGroupType.registrant), {
      [NotificationOptInGroupKeys.payload]: { conferenceDbId },
      [NotificationOptInGroupKeys.filterExpression]: gridFilter ? convertFilter(gridFilter) : null,
    });
    if (gridFilter) {
      filter.push(DxFilterOperators.and, gridFilter);
    }
    const attendees = (await dataSource.store().load({ filter })) as Attendee[];
    const conference = await firstValueFrom(this.attendeesListService.conference$);
    const registrants = attendees
      ?.map(attendee => attendee?.registrant)
      .filter(registrant => this.shouldSendEmailToRegistrant(e.itemData, registrant, conference));

    if (!Array.isArray(attendees)) {
      // TODO
      return;
    }

    if (!registrants?.length) {
      await custom({
        title: 'No recipients',
        messageHtml: 'No invitees match the selected email template criteria.',
        buttons: [{ text: 'OK', onClick: () => true }],
      }).show();
      return;
    }

    await this.attendeesEmailsService.sendEmails(e.itemData, { registrants, optInGroup });
  };

  private shouldSendEmailToRegistrant = (emailTemplate, registrant: Registrant, conference: Conference): boolean => {
    const templateName = emailTemplate?.[EmailTemplateConfigurationKeys.templateName] ?? emailTemplate?.templateName;

    const activitiesTemplates = new Set<EmailTemplates>([
      EmailTemplates.notifyEmailInviteesActivities,
      EmailTemplates.notifyEmailInviteesActivitiesFirstDay,
      EmailTemplates.notifyEmailInviteesActivitiesSecondDay,
    ]);

    if (!activitiesTemplates.has(templateName)) {
      return true;
    }

    const activitiesDay = emailTemplate?.[EmailTemplateConfigurationKeys.payload]?.activitiesDay;

    if (!activitiesDay) {
      return false;
    }

    const chairmanConferenceExcursions =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.excursions] ??
      [];
    const isChairmanExcursionsDay = chairmanConferenceExcursions.some(
      excursion => excursion?.[ExcursionKeys.date] === activitiesDay,
    );

    if (!isChairmanExcursionsDay) {
      return true;
    }

    const registrantData = registrant?.[RegistrantModelKeys.data];
    const isChairmanExtensionEnabled = conference?.[ConferenceKeys.isChairmanExtensionEnabled];
    const isApplicableForChairmanExtension = registrantData?.[RegistrantKeys.isApplicableForChairmanExtension];
    const chairmanExtensionInviteeStatus =
      registrantData?.[RegistrantKeys.chairmanExtension]?.[RegistrantChairmanExtensionKeys.inviteeStatus];

    const isChairmanExtensionAccepted =
      isChairmanExtensionEnabled &&
      isApplicableForChairmanExtension &&
      chairmanExtensionInviteeStatus !== InviteeStatus.declined;

    if (!isChairmanExtensionAccepted) {
      return false;
    }

    const chairmanSelectedExcursions =
      registrantData?.[RegistrantKeys.chairmanExtension]?.[RegistrantChairmanExtensionKeys.selectedExcursions];

    if (!chairmanSelectedExcursions) {
      return false;
    }

    const chairmanExcursionsById = new Map(
      chairmanConferenceExcursions.map(excursion => [excursion?.[ExcursionKeys.id], excursion]),
    );

    return Object.keys(chairmanSelectedExcursions).some(excursionId => {
      if (excursionId.endsWith(AT_LEISURE_EXCURSION_SUFFIX)) {
        const dateString = excursionId.replace(AT_LEISURE_EXCURSION_SUFFIX, 'T00:00:00');
        return dateString === activitiesDay;
      }

      return chairmanExcursionsById.get(excursionId)?.[ExcursionKeys.date] === activitiesDay;
    });
  };

  protected calculateCustomSummary = (options: any) => {
    switch (options.name) {
      case AttendeeKeys.registrationState:
        if (options.summaryProcess === 'start') {
          options.totalValue = 0;
        }
        if (options.summaryProcess === 'calculate') {
          if (options.value[AttendeeKeys.attendeeType] === AttendeeType.Invitee) {
            options.totalValue += 1;
          }
        }
        break;
      default:
        break;
    }
  };

  protected readonly EntityPermissionModelKeys = EntityPermissionModelKeys;
  protected readonly Entity = Entity;
  protected readonly ExcursionInfoTableDataKeys = ExcursionInfoTableDataKeys;
}
